import type { JSX } from 'react';
import { useState } from 'react';
import { ActionIcon, Button, Group, Menu, Modal, Stack, Table, Text, TextInput } from '@mantine/core';
import { type FormulaFunctions, evaluateFormula } from '@pst/shared';
import type { CostItem } from '../../api';
import { formatQuantity } from '../../format';
import { useContextMenu } from '../../ui/ContextMenu';

type Row = { text: string; formula: string };

/**
 * A quantity worked out in rows - "Nordseite 12,5*3", "abzgl. Tor -4*2,5" -
 * as on a take-off sheet. The rows add up to the line's quantity. Removing
 * every row goes back to a single quantity.
 */
export function TakeoffModal({ item, variables, functions, onClose, onSave }: {
  item: CostItem;
  variables: Record<string, number>;
  functions: FormulaFunctions;
  onClose: () => void;
  onSave: (rows: Row[] | null) => void;
}): JSX.Element {
  const start: Row[] = item.takeoff && item.takeoff.length > 0
    ? item.takeoff
    : [{ text: '', formula: item.quantityFormula ?? '' }, { text: '', formula: '' }];
  const [rows, setRows] = useState<Row[]>(start);
  const results = rows.map((r) => (r.formula.trim() ? evaluateFormula(r.formula, variables, functions) : null));
  const bad = results.some((r) => r && !r.ok);
  const sum = results.reduce((s, r) => s + (r && r.ok ? r.value : 0), 0);
  const set = (i: number, patch: Partial<Row>): void => setRows((all) => all.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const rowMenu = useContextMenu<number>((i, close) => {
    const run = (f: () => void) => () => { close(); f(); };
    return (
      <>
        <Menu.Item onClick={run(() => setRows((all) => [...all.slice(0, i + 1), { text: '', formula: '' }, ...all.slice(i + 1)]))}>Zeile darunter einfügen</Menu.Item>
        <Menu.Item onClick={run(() => set(i, { formula: '' }))}>Menge dieser Zeile löschen</Menu.Item>
        <Menu.Item color="red" onClick={run(() => setRows((all) => all.filter((_, j) => j !== i)))}>Zeile löschen</Menu.Item>
      </>
    );
  });
  const filled = rows.filter((r) => r.formula.trim() !== '');

  return (
    <Modal opened onClose={onClose} title={`Mengenermittlung: ${item.description}`} size="lg">
      <Stack gap="sm">
        <Table verticalSpacing={2} className="pst-tabular">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Bezeichnung</Table.Th>
              <Table.Th w={200}>Ansatz</Table.Th>
              <Table.Th w={110} ta="right">Menge</Table.Th>
              <Table.Th w={30} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r, i) => {
              const res = results[i];
              return (
                <Table.Tr key={i} onContextMenu={(e) => rowMenu.open(e, i)}>
                  <Table.Td><TextInput size="xs" value={r.text} placeholder="z. B. Achse A–C" onChange={(e) => set(i, { text: e.currentTarget.value })} /></Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs" value={r.formula} placeholder="z. B. 12,5*3 oder -4*2,5" error={res && !res.ok ? res.error : undefined}
                      styles={{ input: { fontFamily: 'monospace' } }}
                      onChange={(e) => set(i, { formula: e.currentTarget.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter' && i === rows.length - 1) { e.preventDefault(); setRows((all) => [...all, { text: '', formula: '' }]); } }}
                    />
                  </Table.Td>
                  <Table.Td ta="right"><Text size="sm">{res && res.ok ? formatQuantity(res.value) : ''}</Text></Table.Td>
                  <Table.Td><ActionIcon size="xs" variant="subtle" color="gray" aria-label="Zeile löschen" onClick={() => setRows((all) => all.filter((_, j) => j !== i))}>✕</ActionIcon></Table.Td>
                </Table.Tr>
              );
            })}
            <Table.Tr style={{ borderTop: '2px solid var(--mantine-color-default-border)' }}>
              <Table.Td colSpan={2}><Text size="sm" fw={700}>Summe{item.unit ? ` (${item.unit})` : ''}</Text></Table.Td>
              <Table.Td ta="right"><Text size="sm" fw={700}>{formatQuantity(Math.round(sum * 10000) / 10000)}</Text></Table.Td>
              <Table.Td />
            </Table.Tr>
          </Table.Tbody>
        </Table>
        <Group justify="space-between">
          <Button size="xs" variant="subtle" onClick={() => setRows((all) => [...all, { text: '', formula: '' }])}>+ Zeile</Button>
          <Group gap="xs">
            {item.takeoff && item.takeoff.length > 0 && <Button size="xs" variant="subtle" color="gray" onClick={() => onSave(null)}>Mengenermittlung entfernen</Button>}
            <Button size="xs" variant="default" onClick={onClose}>Abbrechen</Button>
            <Button size="xs" disabled={bad} onClick={() => onSave(filled.length > 0 ? filled.map((r) => ({ text: r.text.trim(), formula: r.formula.trim() })) : null)}>Übernehmen</Button>
          </Group>
        </Group>
        <Text size="xs" c="dimmed">Abzüge mit Minus. Namen aus den Mengen und Kennwerten sind erlaubt. Enter in der letzten Zeile legt eine neue an.</Text>
      </Stack>
      {rowMenu.menu}
      </Modal>
  );
}
